
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  moduleName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Enhanced Error Boundary with module isolation
 * Prevents errors in one module from crashing the entire application
 */
export class ModuleErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, moduleName } = this.props;
    
    // Log error with module context
    console.error(`Error in module ${moduleName || 'Unknown'}:`, error, errorInfo);
    
    // Report to error tracking service
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: `Module: ${moduleName}, Error: ${error.message}`,
        fatal: false
      });
    }

    // Custom error handler
    if (onError) {
      onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback, moduleName } = this.props;
      
      if (fallback) {
        return fallback;
      }

      return (
        <div className="p-4 border border-red-300 rounded-lg bg-red-50">
          <h3 className="text-red-800 font-medium">
            Erro no módulo {moduleName || 'desconhecido'}
          </h3>
          <p className="text-red-600 text-sm mt-1">
            Algo deu errado. Por favor, recarregue a página ou tente novamente.
          </p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundaries
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  moduleName: string
) {
  const WrappedComponent = (props: P) => (
    <ModuleErrorBoundary moduleName={moduleName}>
      <Component {...props} />
    </ModuleErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
}
